// Production bridge: F2's CallEngine with SIP.js, real media devices, the Web Locks
// one-phone-per-profile lock and page lifecycle. Loaded lazily so SIP.js stays out of the
// first page load and is fetched only once a device exists.
import type { BridgeContext } from '../state/workspace.tsx';
import { allowedSipHosts, CLIENT_VERSION } from '../config/phone.ts';
import { PHONE_RECONNECT_POLICY } from '../lib/backoff.ts';
import type { CallBridge } from './bridge.ts';
import { EngineBridge, type EngineFactory } from './engineBridge.ts';
import { iphoneSafari, iphoneHomeScreen } from './iphone.ts';

async function loadBrowserEngineFactory(request: BridgeContext['api']['request']): Promise<EngineFactory> {
  const [{ CallEngine, DeviceCredentialSource }, browser] = await Promise.all([import('@bcio-voice/call-client/core'), import('@bcio-voice/call-client')]);
  return input => {
    const lock = browser.createWebLocksDeviceLock();
    // F2's canonical credential source: memory only, rate-limited rotation, seeded so creation does not rotate.
    const credentials = new DeviceCredentialSource({ issue: input.issueCredentials });
    if (input.initialDevice) credentials.seed(input.initialDevice);
    return new CallEngine({
      deviceId: input.deviceId,
      userId: input.userId,
      clientVersion: input.clientVersion,
      api: input.api,
      bootstrapSource: credentials,
      ...(input.initialDevice ? { initialBootstrap: input.initialDevice } : {}),
      sip: browser.createSipJsTransportFactory({signalingRequest:(path,init)=>request(path.replace(/^\/api\/v1/,''),init)}),
      media: browser.createBrowserMediaPlatform(),
      lifecycle: browser.createBrowserLifecycle(),
      ...(lock ? { deviceLock: lock } : {}),
      capabilities: input.capabilities,
      policy: { ...PHONE_RECONNECT_POLICY, allowedSipHosts: input.allowedSipHosts, requireMicrophoneBeforeRegister: false, retainMicrophoneForSession: iphoneSafari() || iphoneHomeScreen() },
    });
  };
}

export function createBrowserBridge(context: BridgeContext): CallBridge {
  return new EngineBridge({
    api: context.api,
    userId: context.userId,
    clientVersion: CLIENT_VERSION,
    allowedSipHosts: allowedSipHosts(),
    loadFactory: () => loadBrowserEngineFactory(context.api.request.bind(context.api)),
    capabilities: context.capabilities,
    onResync: context.onResync,
    onRevoked: context.onRevoked,
  });
}
